import { FileText } from "lucide-react";
import { inject, observer } from "mobx-react";
import * as React from "react";
import { FormattedNumber } from "react-intl";
import { Link } from "react-router-dom";
import { Card, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, useToast } from "randmarcomps";
import {
  useGetV4WarehousesQuery,
  usePostV4ManufacturerByRouteManufacturerIdPurchaseOrderAndPurchaseOrderNumberEditItemRandmarSkuMutation,
  usePostV4ManufacturerByRouteManufacturerIdRequisitionAndRequisitionNumberEditItemRandmarSkuMutation,
} from "randmar-api-client";
import withManufacturer from "../../contexts/WithManufacturer";
import Path from "../../routes/Path";
import { PurchaseOrderPartNumber, RequisitionPartNumber } from "../../stores/document/Document";
import { RootStore } from "../../stores/rootStore";
import ButtonWithDialogConfirm from '../util/ButtonWithDialogConfirm';
import CurrencyUtils from "../util/CurrencyUtils";
import FormatDateFromInt from "../util/DateTimeUtil";

interface IProps {
  store?: RootStore;
  purchaseOrdersPartNumbers?: PurchaseOrderPartNumber[];
  requisitionsPartNumbers?: RequisitionPartNumber[];
  applicationId: string;
}

const OpenPurchaseOrdersGrid: React.FC<IProps> = ({ store, purchaseOrdersPartNumbers, requisitionsPartNumbers, applicationId }) => {
  const { productStore } = store!;
  const { toast } = useToast();
  const { data: warehouses } = useGetV4WarehousesQuery();

  const [editPurchaseOrderItem] = usePostV4ManufacturerByRouteManufacturerIdPurchaseOrderAndPurchaseOrderNumberEditItemRandmarSkuMutation();
  const [editRequisitionItem] = usePostV4ManufacturerByRouteManufacturerIdRequisitionAndRequisitionNumberEditItemRandmarSkuMutation();

  const getWarehouseNameFromCode = React.useCallback((code?: string): string => {
    if (!code || !warehouses) {
      return "B/O";
    }
    const warehouse = warehouses.find(w => w.WarehouseId === code);
    return warehouse?.Name || "B/O";
  }, [warehouses]);

  const handleRemoveFromPurchaseOrder = React.useCallback(async (purchaseOrderNumber: string, randmarSKU: string) => {
    try {
      await editPurchaseOrderItem({
        routeManufacturerId: applicationId,
        purchaseOrderNumber,
        randmarSku: randmarSKU,
        quantity: 0,
      }).unwrap();
      toast({ variant: "success", title: "Success", description: "Item removed from purchase order." });
    } catch {
      toast({ variant: "destructive", title: "Error", description: "Failed to remove item from purchase order." });
    }

    await productStore.getProduct(applicationId, randmarSKU);
  }, [applicationId, editPurchaseOrderItem, productStore, toast]);

  const handleRemoveFromRequisition = React.useCallback(async (requisitionNumber: string, randmarSKU: string) => {
    try {
      await editRequisitionItem({
        routeManufacturerId: applicationId,
        requisitionNumber,
        randmarSku: randmarSKU,
        quantity: 0,
      }).unwrap();
      toast({ variant: "success", title: "Success", description: "Item removed from requisition." });
    } catch {
      toast({ variant: "destructive", title: "Error", description: "Failed to remove item from requisition." });
    }

    await productStore.getProduct(applicationId, randmarSKU);
  }, [applicationId, editRequisitionItem, productStore, toast]);

  if ((purchaseOrdersPartNumbers || []).length + (requisitionsPartNumbers || []).length === 0) {
    return <div>There are no pending purchase orders at the present time...</div>;
  }

  return (
    <Card className="w-full">
      <div className="w-full overflow-x-auto rounded-2xl border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10" />
              <TableHead className="text-center">Location</TableHead>
              <TableHead className="text-center">Part Number</TableHead>
              <TableHead className="text-center">Date</TableHead>
              <TableHead className="text-center">Quantity</TableHead>
              <TableHead className="text-center">Unit Price</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {requisitionsPartNumbers && requisitionsPartNumbers
              .slice()
              .map((row, index) => (
                <TableRow className="hover:bg-muted/50" key={"Requisition" + index}>
                  <TableCell className="text-center">
                    <Link title={`Draft purchase ${row.requisitionNumber}`} to={Path.document(applicationId, row.requisitionNumber)}>
                      <FileText className="inline h-4 w-4 text-muted-foreground" aria-hidden="true" />
                    </Link>
                  </TableCell>
                  <TableCell className="text-center">{getWarehouseNameFromCode(row.location)}</TableCell>
                  <TableCell className="text-center">
                    <Link to={Path.product(applicationId, row.randmarSKU)} className="font-medium underline underline-offset-2">
                      {row.mpn}
                    </Link>
                  </TableCell>
                  <TableCell className="text-center">N/A</TableCell>
                  <TableCell className="text-center">{row.quantityOrdered}</TableCell>
                  <TableCell className="text-center"><FormattedNumber {...CurrencyUtils.format} value={row.unitPrice || 0} /></TableCell>
                  <TableCell className="text-center">
                    <ButtonWithDialogConfirm
                      fab={true}
                      onConfirmation={() => handleRemoveFromRequisition(row.requisitionNumber || "", row.randmarSKU || "")}
                      buttonTitle={`Remove ${row.mpn} from ${row.requisitionNumber}`}
                    />
                  </TableCell>
                </TableRow>
              ))}
            {purchaseOrdersPartNumbers && purchaseOrdersPartNumbers
              .slice()
              .map((row, index) => (
                <TableRow className="hover:bg-muted/50" key={"PurchaseOrder" + index}>
                  <TableCell className="text-center">
                    <Link title="Purchase Order Number" to={Path.document(applicationId, row.purchaseOrderNumber)} className="font-medium underline underline-offset-2">
                      {row.purchaseOrderNumber}
                    </Link>
                  </TableCell>
                  <TableCell className="text-center">{getWarehouseNameFromCode(row.location)}</TableCell>
                  <TableCell className="text-center">
                    <Link to={Path.product(applicationId, row.randmarSKU)} className="font-medium underline underline-offset-2">
                      {row.mpn}
                    </Link>
                  </TableCell>
                  <TableCell className="text-center">{FormatDateFromInt(row.purchaseOrderDate)}</TableCell>
                  <TableCell className="text-center">{row.quantity}</TableCell>
                  <TableCell className="text-center"><FormattedNumber {...CurrencyUtils.format} value={row.unitPrice || 0} /></TableCell>
                  <TableCell className="text-center">
                    <ButtonWithDialogConfirm
                      fab={true}
                      onConfirmation={() => handleRemoveFromPurchaseOrder(row.purchaseOrderNumber || "", row.randmarSKU || "")}
                      buttonTitle={`Remove ${row.mpn} from ${row.purchaseOrderNumber}`}
                    />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
};

export default withManufacturer(inject("store")(observer(OpenPurchaseOrdersGrid)));
